<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import MetricHint from './MetricHint.vue';
import CompanyConversationList from './CompanyConversationList.vue';

const props = defineProps({
  companyId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const isLoading = ref(false);
const breakdown = ref({
  openConversationsCount: 0,
  resolvedConversationsCount: 0,
  contactsCount: 0,
  lastActivityAt: null,
  agents: [],
  contacts: [],
  openConversations: [],
  resolvedConversations: [],
});

const since = computed(() => Number(route.query.from) || 0);
const until = computed(() => Number(route.query.to) || 0);
const businessHours = computed(() => route.query.business_hours === 'true');
const emptyValue = computed(() => t('COMPANY_REPORTS.EMPTY_VALUE'));

const formatTimestamp = value =>
  value ? shortTimestamp(dynamicTime(value), true) : emptyValue.value;

const lastActivityLabel = computed(() =>
  formatTimestamp(breakdown.value.lastActivityAt)
);

const agentRows = computed(() =>
  (breakdown.value.agents || []).map(agent => ({
    ...agent,
    name: agent.name || t('COMPANY_REPORTS.UNASSIGNED_AGENT'),
  }))
);

const fetchBreakdown = async () => {
  if (!props.companyId || !since.value || !until.value) return;

  isLoading.value = true;
  try {
    const data = await store.dispatch('summaryReports/fetchCompanyBreakdown', {
      id: props.companyId,
      since: since.value,
      until: until.value,
      businessHours: businessHours.value,
    });
    breakdown.value = data || breakdown.value;
  } catch {
    useAlert(t('REPORT.SUMMARY_FETCHING_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

watch([() => props.companyId, since, until, businessHours], fetchBreakdown, {
  immediate: true,
});
</script>

<template>
  <div class="w-full mt-12 pb-8 space-y-8">
    <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
      <div
        class="px-4 py-3 rounded-xl outline outline-1 outline-n-container bg-n-solid-2"
      >
        <MetricHint
          class="text-xs text-n-slate-11"
          :label="$t('COMPANY_REPORTS.METRICS.OPEN')"
          :tooltip="$t('COMPANY_REPORTS.TOOLTIPS.OPEN')"
        />
        <p class="mt-1 text-xl font-medium text-n-slate-12">
          {{ breakdown.openConversationsCount || 0 }}
        </p>
      </div>
      <div
        class="px-4 py-3 rounded-xl outline outline-1 outline-n-container bg-n-solid-2"
      >
        <MetricHint
          class="text-xs text-n-slate-11"
          :label="$t('COMPANY_REPORTS.METRICS.RESOLVED')"
          :tooltip="$t('COMPANY_REPORTS.TOOLTIPS.RESOLVED')"
        />
        <p class="mt-1 text-xl font-medium text-n-slate-12">
          {{ breakdown.resolvedConversationsCount || 0 }}
        </p>
      </div>
      <div
        class="px-4 py-3 rounded-xl outline outline-1 outline-n-container bg-n-solid-2"
      >
        <MetricHint
          class="text-xs text-n-slate-11"
          :label="$t('COMPANY_REPORTS.METRICS.CONTACTS')"
          :tooltip="$t('COMPANY_REPORTS.TOOLTIPS.CONTACTS')"
        />
        <p class="mt-1 text-xl font-medium text-n-slate-12">
          {{ breakdown.contactsCount || 0 }}
        </p>
      </div>
      <div
        class="px-4 py-3 rounded-xl outline outline-1 outline-n-container bg-n-solid-2"
      >
        <MetricHint
          class="text-xs text-n-slate-11"
          :label="$t('COMPANY_REPORTS.METRICS.LAST_ACTIVITY')"
          :tooltip="$t('COMPANY_REPORTS.TOOLTIPS.LAST_ACTIVITY')"
        />
        <p class="mt-1 text-xl font-medium text-n-slate-12">
          {{ lastActivityLabel }}
        </p>
      </div>
    </div>

    <div v-if="isLoading" class="flex justify-center py-10">
      <Spinner :size="28" class="text-n-brand" />
    </div>

    <div v-else class="space-y-8">
      <div class="grid gap-6 lg:grid-cols-2">
        <CompanyConversationList
          :title="$t('COMPANY_REPORTS.OPEN_CONVERSATIONS_HEADER')"
          :tooltip="$t('COMPANY_REPORTS.TOOLTIPS.OPEN')"
          :conversations="breakdown.openConversations"
          :empty-label="$t('COMPANY_REPORTS.NO_OPEN_CONVERSATIONS')"
          :total-count="breakdown.openConversationsCount || 0"
        />
        <CompanyConversationList
          :title="$t('COMPANY_REPORTS.RESOLVED_CONVERSATIONS_HEADER')"
          :tooltip="$t('COMPANY_REPORTS.TOOLTIPS.RESOLVED')"
          :conversations="breakdown.resolvedConversations"
          :empty-label="$t('COMPANY_REPORTS.NO_RESOLVED_CONVERSATIONS')"
          :total-count="breakdown.resolvedConversationsCount || 0"
        />
      </div>
      <div class="grid gap-6 lg:grid-cols-2">
        <div
          class="overflow-hidden rounded-xl outline outline-1 outline-n-container bg-n-solid-2"
        >
          <h3 class="px-4 py-3 text-sm font-medium text-n-slate-12">
            <MetricHint
              :label="$t('COMPANY_REPORTS.AGENTS_HEADER')"
              :tooltip="$t('COMPANY_REPORTS.TOOLTIPS.AGENTS')"
            />
          </h3>
          <table class="w-full text-sm">
            <thead class="text-left text-n-slate-11">
              <tr>
                <th class="px-4 py-2 font-medium">
                  <MetricHint
                    :label="$t('COMPANY_REPORTS.METRICS.AGENTS')"
                    :tooltip="$t('COMPANY_REPORTS.TOOLTIPS.AGENTS')"
                  />
                </th>
                <th class="px-4 py-2 font-medium">
                  <MetricHint
                    :label="$t('COMPANY_REPORTS.METRICS.CONVERSATIONS')"
                    :tooltip="$t('COMPANY_REPORTS.TOOLTIPS.CONVERSATIONS')"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!agentRows.length">
                <td
                  colspan="2"
                  class="px-4 py-6 text-sm text-center text-n-slate-11"
                >
                  {{ $t('COMPANY_REPORTS.NO_AGENTS') }}
                </td>
              </tr>
              <tr
                v-for="agent in agentRows"
                :key="agent.id || agent.name"
                class="border-t border-n-weak"
              >
                <td class="px-4 py-2 text-n-slate-12">{{ agent.name }}</td>
                <td class="px-4 py-2 text-n-slate-12">
                  {{ agent.conversationsCount }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          class="overflow-hidden rounded-xl outline outline-1 outline-n-container bg-n-solid-2"
        >
          <h3 class="px-4 py-3 text-sm font-medium text-n-slate-12">
            <MetricHint
              :label="$t('COMPANY_REPORTS.CONTACTS_HEADER')"
              :tooltip="$t('COMPANY_REPORTS.TOOLTIPS.CONTACTS')"
            />
          </h3>
          <table class="w-full text-sm">
            <thead class="text-left text-n-slate-11">
              <tr>
                <th class="px-4 py-2 font-medium">
                  <MetricHint
                    :label="$t('COMPANY_REPORTS.METRICS.CONTACT')"
                    :tooltip="$t('COMPANY_REPORTS.TOOLTIPS.CONTACTS')"
                  />
                </th>
                <th class="px-4 py-2 font-medium">
                  <MetricHint
                    :label="$t('COMPANY_REPORTS.METRICS.CONVERSATIONS')"
                    :tooltip="$t('COMPANY_REPORTS.TOOLTIPS.CONVERSATIONS')"
                  />
                </th>
                <th class="px-4 py-2 font-medium">
                  <MetricHint
                    :label="$t('COMPANY_REPORTS.METRICS.LAST_ACTIVITY')"
                    :tooltip="$t('COMPANY_REPORTS.TOOLTIPS.LAST_ACTIVITY')"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!breakdown.contacts?.length">
                <td
                  colspan="3"
                  class="px-4 py-6 text-sm text-center text-n-slate-11"
                >
                  {{ $t('COMPANY_REPORTS.NO_CONTACTS') }}
                </td>
              </tr>
              <tr
                v-for="contact in breakdown.contacts"
                :key="contact.id"
                class="border-t border-n-weak"
              >
                <td class="px-4 py-2">
                  <router-link
                    :to="{
                      name: 'contacts_edit',
                      params: { contactId: contact.id },
                    }"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-n-slate-12 hover:underline"
                  >
                    {{ contact.name }}
                  </router-link>
                </td>
                <td class="px-4 py-2 text-n-slate-12">
                  {{ contact.conversationsCount }}
                </td>
                <td class="px-4 py-2 text-n-slate-12">
                  {{ formatTimestamp(contact.lastActivityAt) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
